import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { ArrowLeft, ExternalLink, Search, Inbox } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { PlaneRefreshHeader } from "@/components/PlaneRefreshHeader";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/format";

interface Ticket {
  id: string;
  name: string;
  state: string;
  stateGroup: "backlog" | "unstarted" | "started" | "completed" | "cancelled" | "unknown";
  priority: "urgent" | "high" | "medium" | "low" | "none";
  dueDate?: string;
  url: string;
  projectId?: string;
  projectName?: string;
  projectIdentifier?: string;
  panelProjectId?: string;
  panelProjectName?: string;
  panelProjectSlug?: string;
}

interface Resp {
  user: { id: string; name: string };
  tickets: Ticket[];
  ticketsMeta: { cachedAt: number; source: "manual" | "auto" } | null;
}

export const Route = createFileRoute("/team/$userId/tickets")({
  component: TeamTicketsPage,
});

const STATE_BADGE: Record<Ticket["stateGroup"], string> = {
  backlog: "badge-ghost",
  unstarted: "badge-info",
  started: "badge-primary",
  completed: "badge-success",
  cancelled: "badge-warning",
  unknown: "badge-ghost",
};

const PRIORITY_LABEL: Record<Ticket["priority"], string> = {
  urgent: "Urgente",
  high: "Alta",
  medium: "Media",
  low: "Baja",
  none: "Sin prioridad",
};

const PRIORITY_BADGE: Record<Ticket["priority"], string> = {
  urgent: "badge-error",
  high: "badge-warning",
  medium: "badge-info",
  low: "badge-ghost",
  none: "badge-ghost",
};

const PRIORITY_ORDER: Record<Ticket["priority"], number> = {
  urgent: 0,
  high: 1,
  medium: 2,
  low: 3,
  none: 4,
};

function TeamTicketsPage() {
  const { userId } = Route.useParams();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery<Resp>({
    queryKey: ["team", userId, "tickets"],
    queryFn: () => api.get(`/team/${userId}/tickets`),
  });
  const refresh = useMutation({
    mutationFn: () => api.get<Resp>(`/team/${userId}/tickets?refresh=1`),
    onSuccess: (fresh) => qc.setQueryData(["team", userId, "tickets"], fresh),
  });
  const [projectFilter, setProjectFilter] = useState<string>("all");
  const [stateFilter, setStateFilter] = useState<string>("all");
  const [q, setQ] = useState("");

  const tickets = data?.tickets ?? [];

  const projectOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const t of tickets) {
      const id = t.projectId ?? "";
      const name = t.panelProjectName ?? t.projectName ?? "(sin proyecto)";
      if (!map.has(id)) map.set(id, name);
    }
    return [...map.entries()]
      .map(([id, name]) => ({ id, name }))
      .toSorted((a, b) => a.name.localeCompare(b.name));
  }, [tickets]);

  const stateOptions = useMemo(() => {
    const map = new Map<string, number>();
    for (const t of tickets) {
      map.set(t.state, (map.get(t.state) ?? 0) + 1);
    }
    return [...map.entries()].toSorted((a, b) => a[0].localeCompare(b[0]));
  }, [tickets]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return tickets
      .filter((t) => {
        if (projectFilter !== "all" && (t.projectId ?? "") !== projectFilter) return false;
        if (stateFilter !== "all" && t.state !== stateFilter) return false;
        if (needle) {
          const hay =
            `${t.name} ${t.state} ${t.projectName ?? ""} ${t.panelProjectName ?? ""}`.toLowerCase();
          if (!hay.includes(needle)) return false;
        }
        return true;
      })
      .toSorted((a, b) => {
        // Atrasados primero, luego por prioridad, luego por dueDate asc.
        const today = new Date().toISOString().slice(0, 10);
        const aOverdue = a.dueDate && a.dueDate < today ? 0 : 1;
        const bOverdue = b.dueDate && b.dueDate < today ? 0 : 1;
        if (aOverdue !== bOverdue) return aOverdue - bOverdue;
        if (PRIORITY_ORDER[a.priority] !== PRIORITY_ORDER[b.priority]) {
          return PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority];
        }
        if (a.dueDate && b.dueDate) return a.dueDate.localeCompare(b.dueDate);
        if (a.dueDate) return -1;
        if (b.dueDate) return 1;
        return 0;
      });
  }, [tickets, projectFilter, stateFilter, q]);

  if (isLoading || !data) {
    return (
      <div className="grid place-items-center py-20">
        <span className="loading loading-spinner loading-lg text-primary" />
      </div>
    );
  }

  const todayIso = new Date().toISOString().slice(0, 10);

  return (
    <div className="space-y-5">
      <PageHeader
        eyebrow="tickets de compañero"
        title={`Tickets de ${data.user.name}`}
        subtitle={`${tickets.length} ticket${tickets.length === 1 ? "" : "s"} activo${tickets.length === 1 ? "" : "s"} (excluye cerrados).`}
        actions={
          <Link to="/team/$userId" params={{ userId }} className="btn btn-ghost btn-sm">
            <ArrowLeft className="size-4" />
            Volver al perfil
          </Link>
        }
      />

      <PlaneRefreshHeader
        meta={data.ticketsMeta}
        isPending={refresh.isPending}
        onRefresh={() => refresh.mutate()}
      />

      <div className="card-elevated p-3 flex flex-wrap items-center gap-2">
        <label className="input input-bordered input-sm flex items-center gap-2 max-w-xs flex-1">
          <Search className="size-3.5 text-base-content/40" />
          <input
            type="text"
            placeholder="Buscar por título / proyecto"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="grow"
          />
        </label>
        <select
          value={projectFilter}
          onChange={(e) => setProjectFilter(e.target.value)}
          className="select select-bordered select-sm"
        >
          <option value="all">Todos los proyectos</option>
          {projectOptions.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
        <select
          value={stateFilter}
          onChange={(e) => setStateFilter(e.target.value)}
          className="select select-bordered select-sm"
        >
          <option value="all">Todos los estados</option>
          {stateOptions.map(([state, count]) => (
            <option key={state} value={state}>
              {state} · {count}
            </option>
          ))}
        </select>
        <span className="ml-auto text-xs text-base-content/55 tabular-nums">
          {filtered.length} de {tickets.length}
        </span>
      </div>

      {filtered.length === 0 ? (
        <div className="card-elevated p-10 text-center text-base-content/55">
          <Inbox className="size-10 mx-auto mb-3 opacity-30" />
          {tickets.length === 0
            ? "Este compañero no tiene tickets activos en Plane."
            : "Ningún ticket coincide con los filtros."}
        </div>
      ) : (
        <ul className="card-elevated divide-y divide-base-300/40">
          {filtered.map((t) => {
            const overdue = !!t.dueDate && t.dueDate < todayIso;
            return (
              <li key={t.id} className="px-4 py-3 flex items-start gap-3 text-sm">
                <a
                  href={t.url}
                  target="_blank"
                  rel="noreferrer"
                  className="min-w-0 flex-1 hover:text-primary transition-colors"
                >
                  <div className="font-medium leading-snug break-words">{t.name}</div>
                  <div className="text-xs text-base-content/55 mt-1 flex items-center gap-1.5 flex-wrap">
                    {t.panelProjectName ? (
                      <span
                        className="badge badge-primary badge-xs"
                        title={`En Plane: ${t.projectName ?? "—"}`}
                      >
                        {t.panelProjectName}
                      </span>
                    ) : (
                      t.projectName && (
                        <span
                          className="badge badge-ghost badge-xs"
                          title={t.projectIdentifier ?? undefined}
                        >
                          {t.projectName}
                        </span>
                      )
                    )}
                    <span className={`badge badge-xs ${STATE_BADGE[t.stateGroup]}`}>{t.state}</span>
                    {t.priority !== "none" && (
                      <span className={`badge badge-xs ${PRIORITY_BADGE[t.priority]}`}>
                        {PRIORITY_LABEL[t.priority]}
                      </span>
                    )}
                    {t.dueDate && (
                      <span className={overdue ? "text-error font-medium" : ""}>
                        {overdue ? "Atrasado · " : "Vence "}
                        {formatDate(t.dueDate)}
                      </span>
                    )}
                  </div>
                </a>
                <a
                  href={t.url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-base-content/30 hover:text-primary shrink-0 pt-0.5"
                  aria-label="Abrir en Plane"
                >
                  <ExternalLink className="size-3.5" />
                </a>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
